import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Welcome from './views/Welcome';
import ChatInterface from './views/ChatInterface';
import Signup from './views/Signup';
import Sidebar from './components/Sidebar';
import HeaderCredits from './components/HeaderCredits';
import packageJson from '../package.json';
import { isAuthenticated, setCookieAuthVerified, hasExplicitlyLoggedOut, getAuthHeaders } from './utils/auth';
import { API_ENDPOINTS } from './config/api';
import UploadPage from './views/UploadPage';
import InterviewSessions from './views/InterviewSessions';
import BuyCredits from './views/BuyCredits';
import Profile from './views/Profile';
import RefundPolicy from './views/RefundPolicy';
import DesktopDownload from './views/DesktopDownload';
import TermsAndConditions from './views/TermsAndConditions';
import PrivacyPolicy from './views/PrivacyPolicy';
import CurrentUserDetails from './views/CurrentUserDetails';
import EmailCampaigns from './views/EmailCampaigns';
import JobManagement from './views/JobManagement';
import ViewJobs from './views/ViewJobs';
import CandidateMatching from './views/CandidateMatching';
import PaymentSuccess from './views/PaymentSuccess';
import ReferralHistory from './views/ReferralHistory';
import SessionTranscript from './views/SessionTranscript';
import InternshipApplications from './views/InternshipApplications';

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuth, setIsAuth] = useState(false);
  const [authHydrated, setAuthHydrated] = useState(false); // CRITICAL: Prevents routing before auth check
  // eslint-disable-next-line no-unused-vars
  const versionLabel = `v${process.env.REACT_APP_VERSION || packageJson.version || '0.0.0'}`;

  // Capture referral code from URL (?ref=CODE) on any landing page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem('referralCode', refCode);
      // Clean ref param from URL and redirect to buy-credits
      window.history.replaceState(null, '', '/buy-credits');
    }
  }, []);
  
  const handleNavigateToUpload = () => navigate('/upload');
  const handleNavigateToHome = () => navigate('/home');
  const handleNavigateToSessions = () => navigate('/sessions');

  // CRITICAL: Hydrate auth state SYNCHRONOUSLY on first render to prevent production redirect bugs
  useEffect(() => {
    const checkAuth = () => {
      setIsAuth(isAuthenticated());
    };
    
    // CRITICAL: Verify cookie-based auth by making an API call
    // HttpOnly cookies can't be read by JavaScript, so we verify by calling the API
    const verifyCookieAuth = async () => {
      // Don't auto-reauth if user explicitly logged out
      if (hasExplicitlyLoggedOut()) {
        checkAuth();
        setAuthHydrated(true);
        window.dispatchEvent(new Event('authHydrated'));
        return;
      }
      
      try {
        // ✅ IMPORTANT: Call /api/auth/me FIRST to validate token against database
        // This ensures cross-browser logout works - if token was cleared in another browser,
        // /api/auth/me will return 401 and we'll log out here too
        console.log('🔐 [App] Verifying session with /api/auth/me...');
        const meResponse = await fetch(API_ENDPOINTS.AUTH.ME, {
          method: 'GET',
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        
        if (meResponse.status === 401) {
          console.warn('🔐 [App] Got 401 from /api/auth/me - server session invalid');
          
          // Only force full logout if user has NO local auth token to fall back on.
          // If auth_token exists in localStorage the user can keep working with
          // token-based auth; the token is validated on every individual API call.
          const hasLocalToken = !!localStorage.getItem('auth_token');
          if (!hasLocalToken) {
            // Pure cookie-auth user – cookie was rejected, clear UI data
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userName');
            localStorage.removeItem('userPicture');
            localStorage.removeItem('userId');
            localStorage.removeItem('cookieAuthActive');
          }
          
          setCookieAuthVerified(false);
          checkAuth(); // re-evaluate from current localStorage
          setAuthHydrated(true);
          window.dispatchEvent(new Event('authHydrated'));
          return;
        }
        
        if (!meResponse.ok) {
          console.warn('🔐 [App] /api/auth/me returned status:', meResponse.status);
          // Non-401 server error – don't touch auth state, fall back to local auth
          checkAuth();
          setAuthHydrated(true);
          window.dispatchEvent(new Event('authHydrated'));
          return;
        }
        
        // Token is valid, get user info from /api/auth/me
        const userData = await meResponse.json();
        console.log('✅ [App] User authenticated via /api/auth/me:', userData.email);
        
        // Update localStorage with user data
        if (userData.email) localStorage.setItem('userEmail', userData.email);
        if (userData.name) localStorage.setItem('userName', userData.name);
        if (userData.pictureUrl) localStorage.setItem('userPicture', userData.pictureUrl);
        if (userData.userId || userData.id) localStorage.setItem('userId', (userData.userId || userData.id).toString());
        
        // Now fetch credits
        const creditsResponse = await fetch(API_ENDPOINTS.USERS.CREDITS, {
          method: 'GET',
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        
        let creditsValue = 0;
        if (creditsResponse.ok) {
          const creditsData = await creditsResponse.json();
          creditsValue = creditsData.totalCredits ?? creditsData.credits ?? 0;
          console.log('✅ [App] Credits fetched:', creditsValue);
        } else {
          console.warn('⚠️ [App] Could not fetch credits, status:', creditsResponse.status);
        }
        
        // Authentication successful – refresh the 7-day client-side session window
        localStorage.setItem('auth_login_time', Date.now().toString());
        setCookieAuthVerified(true);
        setIsAuth(true);
        
        // Dispatch events to refresh credits and notify components
        window.dispatchEvent(new Event('authChanged'));
        window.dispatchEvent(new CustomEvent('creditsUpdated', { 
          detail: { totalCredits: creditsValue } 
        }));
        
        setAuthHydrated(true);
        window.dispatchEvent(new Event('authHydrated'));
        
      } catch (error) {
        console.error('❌ [App] Error during auth verification:', error);
        // Network error – don't clear auth state; keep using whatever local auth exists
        checkAuth();
        setAuthHydrated(true);
        window.dispatchEvent(new Event('authHydrated'));
      }
    };
    
    // First do a quick localStorage check
    checkAuth();
    
    // Then verify cookie auth asynchronously
    verifyCookieAuth();
    
    // Listen for storage and auth changes
    window.addEventListener('storage', checkAuth);
    window.addEventListener('authChanged', checkAuth);
    const interval = setInterval(checkAuth, 60000); // re-check periodically for token expiry
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authChanged', checkAuth);
      clearInterval(interval);
    };
  }, []);

  // Hide sidebar on signup and chat pages
  const showSidebar = location.pathname !== '/signup' && location.pathname !== '/chat';

  // CRITICAL: Don't render routes until auth state is hydrated from localStorage
  // This prevents the production bug where users are redirected to login despite having valid tokens
  if (!authHydrated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#0b0f0d',
        color: '#48d02a'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid rgba(72, 208, 42, 0.3)',
            borderTop: '3px solid #48d02a',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <>
      {showSidebar && (
        <Sidebar 
          onNavigateToUpload={handleNavigateToUpload} 
          onNavigateToHome={handleNavigateToHome}
          onNavigateToSessions={handleNavigateToSessions}
        />
      )}
      {/* Header credits bar - show Get Credits always, Interview Credits only when authenticated */}
      {showSidebar && (
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          background: '#0b0f0d',
          padding: 'clamp(8px, 2vw, 12px) clamp(16px, 4vw, 32px) clamp(6px, 1.5vw, 8px) clamp(60px, 15vw, 0px)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <HeaderCredits isAuthenticated={isAuth} />
        </div>
      )}
      <Routes>
        <Route path="/" element={<Welcome />} />
        <Route path="/home" element={<Welcome />} />
        <Route path="/chat" element={<ChatInterface />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/sessions" element={<InterviewSessions />} />
        <Route path="/sessions/:sessionId/transcript" element={<SessionTranscript />} />
        <Route path="/buy-credits" element={<BuyCredits />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/desktop-download" element={<DesktopDownload />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/current-user-details" element={<CurrentUserDetails />} />
        <Route path="/email-campaigns" element={<EmailCampaigns />} />
        <Route path="/job-management" element={<JobManagement />} />
        <Route path="/candidate-matching" element={<CandidateMatching />} />
        <Route path="/jobs" element={<ViewJobs />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/referral-history" element={<ReferralHistory />} />
        <Route path="/internship-applications" element={<InternshipApplications />} />
        <Route path="/premium" element={<div style={{ padding: 24 }}>Premium Page</div>} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppShell />
      </Router>
    </HelmetProvider>
  );
}
